import styled from "styled-components";

import { PageBody } from "../PageBody";
import { Typography } from "../../atoms/Typography";
import { Button } from "../../atoms/Button";
import { NotificationBody } from "../../organisms/notification/NotificationBody";
import { Loading } from "../../atoms/Loading";
import { formatDateTimeJST } from "../../../utils/date";

const ContentWrapper = styled.div`
  padding: 20px 40px;
`;

const FieldGroup = styled.div`
  margin-bottom: 20px;
`;

const FieldLabel = styled.div`
  font-size: ${(props) => props.theme.fontSize.xsmall};
  color: ${(props) => props.theme.colors.good};
  margin-bottom: 4px;
  font-weight: bold;
`;

const FieldValue = styled.div`
  font-size: ${(props) => props.theme.fontSize.small};
  color: ${(props) => props.theme.colors.primaryText};
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Tag = styled.span`
  display: inline-block;
  padding: 3px 8px;
  background: ${(props) => props.theme.colors.pageBodyBackground};
  border: 1px solid ${(props) => props.theme.colors.primaryBorder};
  border-radius: 4px;
  font-size: ${(props) => props.theme.fontSize.xsmall};
`;

const TermsBadge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  background-color: ${(props) => props.theme.colors.pageBodyBackground};
  border: 1px solid ${(props) => props.theme.colors.primaryBorder};
  font-size: 12px;
  margin-left: 8px;
`;

const HeaderActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
`;

const ButtonWrapper = styled.div`
  width: 200px;
  height: 44px;
`;

const DeleteAction = styled.div`
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  text-align: right;
`;

const DeleteLink = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.caution};
  cursor: pointer;
  font-size: ${(props) => props.theme.fontSize.xsmall};
  padding: 0;
  text-decoration: underline;
  &:hover {
    opacity: 0.7;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  margin: 24px 0;
`;

const PreviewSection = styled.div`
  padding: 16px;
  border: 1px solid ${(props) => props.theme.colors.primaryBorder};
  border-radius: 6px;
  background: ${(props) => props.theme.colors.primaryBackground};
`;

const PDT_CODE_LABELS: Record<string, string> = {
  "2184b7b9-2dba-4a97-8fff-ccd7d0c8c40d": "SC",
  "39dd9c55-343f-4810-ba8a-f417b07d6caf": "HT",
  "c1a2b5e0-c0a3-41c3-aadd-7e14043fedb3": "RELIVION",
  "d38a1261-c952-41a7-8333-251498ad2d7f": "HAUDY",
};

const ROLE_LABELS: Record<string, string> = {
  ADMIN: "管理者",
  STAFF: "スタッフ",
};

const POSITION_LABELS: Record<string, string> = {
  DOCTOR: "医師",
  NURSE: "看護師",
  STAFF: "スタッフ",
};

type Notification = {
  id: string;
  title: string;
  body: string;
  medicalInstitutionIds: string[];
  roles: string[];
  positions: string[];
  pdtCodes: string[];
  termsVersionId: string | null;
  isEditable: boolean;
  isDeletable: boolean;
  startFrom: string;
  endAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type NotificationDetailPageProps = {
  loading: boolean;
  notification: Notification | null;
  previewHtml: string;
  onEditClick: () => void;
  onDeleteClick: () => void;
};

const formatDate = formatDateTimeJST;

export function NotificationDetailPage({
  loading,
  notification,
  previewHtml,
  onEditClick,
  onDeleteClick,
}: NotificationDetailPageProps) {
  return (
    <PageBody title="お知らせ詳細">
      {loading || !notification ? (
        <Loading />
      ) : (
        <ContentWrapper>
          {notification.isEditable && (
            <HeaderActions>
              <ButtonWrapper>
                <Button buttonType="primary" onClick={onEditClick}>
                  <Typography size={2} bold>
                    編集
                  </Typography>
                </Button>
              </ButtonWrapper>
            </HeaderActions>
          )}

          <FieldGroup>
            <FieldLabel>タイトル</FieldLabel>
            <FieldValue>
              <Typography size={3} bold>
                {notification.title}
              </Typography>
              {!notification.isEditable && <TermsBadge>編集不可</TermsBadge>}
            </FieldValue>
          </FieldGroup>

          <Divider />

          <FieldGroup>
            <FieldLabel>本文プレビュー</FieldLabel>
            <PreviewSection>
              <NotificationBody body={previewHtml || notification.body} />
            </PreviewSection>
          </FieldGroup>

          <Divider />

          <FieldGroup>
            <FieldLabel>対象医療機関</FieldLabel>
            <FieldValue>
              {notification.medicalInstitutionIds.length === 0
                ? "全医療機関"
                : notification.medicalInstitutionIds.join(", ")}
            </FieldValue>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>対象製品</FieldLabel>
            <TagList>
              {notification.pdtCodes.length === 0 ? (
                <FieldValue>全製品</FieldValue>
              ) : (
                notification.pdtCodes.map((code) => (
                  <Tag key={code}>{PDT_CODE_LABELS[code] || code}</Tag>
                ))
              )}
            </TagList>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>対象ロール</FieldLabel>
            <TagList>
              {notification.roles.length === 0 ? (
                <FieldValue>全ロール</FieldValue>
              ) : (
                notification.roles.map((r) => (
                  <Tag key={r}>{ROLE_LABELS[r] || r}</Tag>
                ))
              )}
            </TagList>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>対象職種</FieldLabel>
            <TagList>
              {notification.positions.length === 0 ? (
                <FieldValue>全職種</FieldValue>
              ) : (
                notification.positions.map((p) => (
                  <Tag key={p}>{POSITION_LABELS[p] || p}</Tag>
                ))
              )}
            </TagList>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>掲載期間</FieldLabel>
            <FieldValue>
              {formatDate(notification.startFrom)} 〜{" "}
              {formatDate(notification.endAt)}
            </FieldValue>
          </FieldGroup>

          {notification.isDeletable && (
            <DeleteAction>
              <DeleteLink onClick={onDeleteClick}>
                このお知らせを削除する
              </DeleteLink>
            </DeleteAction>
          )}
        </ContentWrapper>
      )}
    </PageBody>
  );
}
